import React, { useCallback, useMemo, useState } from "react";
import { parseCsv } from "./csvParse";
import {
  EXPANSION_CSV_DESTINATION_FIELDS,
  EXPANSION_CSV_DESTINATION_GROUP_ORDER,
  EXPANSION_CSV_MATCH_FIELDS,
  buildExpansionAutoColumnMapping,
  groupLabelForExpansionImportField,
  invertExpansionMapping,
} from "./expansionCsvImportFields";
import { importExpansionProjectsFromCsv, mappedExpansionRowToPayload } from "./expansionCsvImport";
import { formatImportPreviewValue, importPreviewPayloadKeys } from "./projectCsvImportDepartments";
import { downloadExpansionImportCsvTemplate } from "./expansionCsvImportTemplate";

const MATCH_FIELD_OPTIONS = EXPANSION_CSV_MATCH_FIELDS;

export default function ExpansionCsvImportPanel() {
  const [step, setStep] = useState("upload");
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [columnToDest, setColumnToDest] = useState({});
  const [matchField, setMatchField] = useState("subsegment_summary.work_order");
  const [createIfMissing, setCreateIfMissing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const destToColumn = useMemo(() => invertExpansionMapping(columnToDest), [columnToDest]);

  const mappedDestIds = useMemo(
    () => new Set(Object.values(columnToDest).filter(Boolean)),
    [columnToDest]
  );

  const matchFieldMapped = mappedDestIds.has(matchField);

  const previewRows = useMemo(() => {
    if (csvRows.length === 0) return [];
    return csvRows.slice(0, 5).map((row, idx) => ({
      key: idx,
      payload: mappedExpansionRowToPayload(row, destToColumn),
    }));
  }, [csvRows, destToColumn]);

  const previewPayloadKeys = useMemo(
    () => importPreviewPayloadKeys(previewRows, [...mappedDestIds]),
    [previewRows, mappedDestIds]
  );

  const onFile = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setStatus("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { headers: h, rows } = parseCsv(String(reader.result || ""));
        if (h.length === 0 || rows.length === 0) {
          setError("CSV is empty or could not be parsed.");
          return;
        }
        const auto = buildExpansionAutoColumnMapping(h);
        setFileName(file.name);
        setHeaders(h);
        setCsvRows(rows);
        setColumnToDest(auto);
        setStep("map");
        const mapped = Object.values(auto).filter(Boolean).length;
        setStatus(`Loaded ${rows.length} rows, ${h.length} columns (${mapped} auto-mapped).`);
      } catch (err) {
        setError(err?.message || "Could not parse CSV.");
      }
    };
    reader.onerror = () => setError("Could not read file.");
    reader.readAsText(file);
  }, []);

  function setMappingForColumn(csvColumn, destinationFieldId) {
    setColumnToDest((prev) => {
      const next = { ...prev, [csvColumn]: destinationFieldId };
      if (destinationFieldId) {
        for (const [col, dest] of Object.entries(next)) {
          if (col !== csvColumn && dest === destinationFieldId) {
            next[col] = "";
          }
        }
      }
      return next;
    });
  }

  function setMappingFromDestination(destFieldId, csvColumn) {
    setColumnToDest((prev) => {
      const next = { ...prev };
      for (const h of headers) {
        if (next[h] === destFieldId) next[h] = "";
      }
      if (destFieldId && csvColumn) next[csvColumn] = destFieldId;
      return next;
    });
  }

  async function runImport() {
    if (!matchFieldMapped) {
      setError(`Map at least one CSV column to "${matchField}" to match existing projects.`);
      return;
    }
    setBusy(true);
    setError("");
    setStatus("Importing…");
    try {
      const result = await importExpansionProjectsFromCsv(csvRows, columnToDest, {
        matchField,
        createIfMissing,
        onProgress: (p) => {
          setStatus(
            `Importing ${p.current}/${p.total} — updated ${p.updated}, created ${p.created}, skipped ${p.skipped}`
          );
        },
      });
      const done = `Done: ${result.updated} updated, ${result.created} created, ${result.skipped} skipped.`;
      if (result.errors.length > 0) {
        setError(`${done} ${result.errors.length} errors. First: ${result.errors[0]}`);
      } else {
        setStatus(done);
      }
      setStep("done");
    } catch (err) {
      setError(err?.message || "Import failed.");
      setStatus("");
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setStep("upload");
    setFileName("");
    setHeaders([]);
    setCsvRows([]);
    setColumnToDest({});
    setStatus("");
    setError("");
  }

  const destinationOptions = useMemo(() => {
    const opts = EXPANSION_CSV_DESTINATION_FIELDS.filter((f) => f.id !== "");
    return [...opts].sort((a, b) => {
      const ai = EXPANSION_CSV_DESTINATION_GROUP_ORDER.indexOf(a.group);
      const bi = EXPANSION_CSV_DESTINATION_GROUP_ORDER.indexOf(b.group);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }, []);

  const destinationOptionGroups = useMemo(() => {
    const map = new Map();
    for (const f of destinationOptions) {
      const g = f.group || "other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(f);
    }
    return EXPANSION_CSV_DESTINATION_GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({
      id: g,
      label: groupLabelForExpansionImportField({ group: g }),
      fields: map.get(g),
    }));
  }, [destinationOptions]);

  return (
    <section className="plugins-card plugins-card-wide" style={{ padding: "1rem 1.25rem" }}>
      <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
        Import expansion projects (CSV)
      </h3>
      <p className="plugins-card-desc">
        Upload a CSV of <strong>expansion subsegments</strong> (e.g. work orders like 6784.07, 6784.08
        grouped under parent 6784). Map columns to expansion summary, subsegment, subphase, and segment
        statistics fields. New rows are marked as CVIN expansion projects. Download the sample template
        for recommended column names.
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: step === "upload" ? 0 : "1rem" }}>
        {step === "upload" ? (
          <label
            className="email-triggers-button email-triggers-button-primary"
            style={{
              display: "inline-block",
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.65 : 1,
            }}
          >
            Choose CSV file
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              disabled={busy}
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: "none",
              }}
            />
          </label>
        ) : null}
        <button
          type="button"
          className="email-triggers-button"
          disabled={busy}
          onClick={() => downloadExpansionImportCsvTemplate()}
        >
          Download sample CSV template
        </button>
      </div>

      {step === "map" || step === "done" ? (
        <>
          <p className="email-triggers-muted">
            File: <strong>{fileName}</strong> — {csvRows.length} data rows
          </p>

          <div
            className="plugins-card"
            style={{ padding: "0.75rem 1rem", marginBottom: "1rem", background: "#f8fafc" }}
          >
            <h4 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem" }}>Match existing projects</h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                <span>Primary match field</span>
                <select
                  value={matchField}
                  onChange={(e) => setMatchField(e.target.value)}
                  disabled={busy}
                  className="email-triggers-select"
                >
                  {MATCH_FIELD_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="plugins-toggle" style={{ marginTop: 18 }}>
                <input
                  type="checkbox"
                  checked={createIfMissing}
                  onChange={(e) => setCreateIfMissing(e.target.checked)}
                  disabled={busy}
                />
                <span>Create new projects when no match</span>
              </label>
            </div>
            {!matchFieldMapped ? (
              <p className="email-triggers-muted" role="alert" style={{ marginTop: 8, color: "#b45309" }}>
                Map a CSV column to <strong>Subsegment work order</strong> below (required to find existing rows).
              </p>
            ) : null}
          </div>

          <h4 style={{ fontSize: "0.95rem", marginBottom: 8 }}>Column mapping</h4>
          <p className="plugins-card-desc" style={{ marginTop: 0 }}>
            For each CSV column, choose which expansion field to import into. Each destination field can
            only be used once.
          </p>

          <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
            <table className="email-triggers-table csv-import-mapping-table">
              <thead>
                <tr>
                  <th>CSV column</th>
                  <th>Sample value</th>
                  <th>Import into (board field)</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((h) => {
                  const sample = csvRows[0]?.[h] ?? "";
                  const dest = columnToDest[h] || "";
                  return (
                    <tr key={h}>
                      <td>
                        <code>{h}</code>
                      </td>
                      <td
                        style={{
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          color: "#64748b",
                        }}
                        title={sample}
                      >
                        {sample || "—"}
                      </td>
                      <td>
                        <select
                          value={dest}
                          onChange={(e) => setMappingForColumn(h, e.target.value)}
                          disabled={busy}
                          className="email-triggers-select"
                          style={{ minWidth: 220 }}
                        >
                          <option value="">— Do not import —</option>
                          {destinationOptionGroups.map((grp) => (
                            <optgroup key={grp.id} label={grp.label}>
                              {grp.fields.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <details style={{ marginBottom: "1rem" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
              Map by board field (alternate view)
            </summary>
            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table className="email-triggers-table">
                <thead>
                  <tr>
                    <th>Board field</th>
                    <th>CSV column</th>
                  </tr>
                </thead>
                <tbody>
                  {destinationOptionGroups.map((grp) => (
                    <React.Fragment key={grp.id}>
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            fontWeight: 600,
                            fontSize: 12,
                            background: grp.id.endsWith("_files") ? "#f0f9ff" : "#f8fafc",
                            color: grp.id.endsWith("_files") ? "#0369a1" : "#475569",
                          }}
                        >
                          {grp.label}
                        </td>
                      </tr>
                      {grp.fields.map((f) => (
                        <tr key={f.id}>
                          <td>{f.label}</td>
                          <td>
                            <select
                              value={destToColumn[f.id] || ""}
                              onChange={(e) => setMappingFromDestination(f.id, e.target.value)}
                              disabled={busy}
                              className="email-triggers-select"
                              style={{ minWidth: 220 }}
                            >
                              <option value="">—</option>
                              {headers.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {previewRows.length > 0 && previewPayloadKeys.length > 0 ? (
            <>
              <h4 style={{ fontSize: "0.95rem", marginBottom: 8 }}>Preview (first rows after mapping)</h4>
              <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
                <table className="email-triggers-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {previewPayloadKeys.map((k) => (
                        <th key={k}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((pr, i) => (
                      <tr key={pr.key}>
                        <td>{i + 1}</td>
                        {previewPayloadKeys.map((k) => (
                          <td key={k} style={{ fontSize: 12, maxWidth: 160 }}>
                            {formatImportPreviewValue(pr.payload[k], k)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            <button
              type="button"
              className="email-triggers-button email-triggers-button-primary"
              disabled={busy || !matchFieldMapped}
              onClick={runImport}
            >
              {busy ? "Importing…" : `Import ${csvRows.length} rows`}
            </button>
            <button type="button" className="email-triggers-button" disabled={busy} onClick={reset}>
              Start over
            </button>
          </div>
        </>
      ) : null}

      {status ? (
        <p className="email-triggers-muted" role="status" style={{ marginTop: "0.75rem" }}>
          {status}
        </p>
      ) : null}
      {error ? (
        <p className="email-triggers-muted" role="alert" style={{ marginTop: "0.75rem" }}>
          {error}
        </p>
      ) : null}
    </section>
  );
}
